// src/components/digital_twin/DigitalTwinControls.js
// --- UPDATE (Digital Twin MOW+ v1) ---------------------------------------------------------
//   FIXES
//   • Reset View flew to {pitch, bearing} only (the page passes no centre / zoom), so it never
//     returned to the network; `onResetView` (the twin's resetCamera) is used when given.
//   • The 2D/3D button kept its own state — after tilting the map by hand it showed the wrong
//     icon and did the opposite. It now follows the map's real pitch.
//   • Focus Mode did nothing when the page passed no toggleFocusMode; it falls back to browser
//     full screen.
//   • Centre on asset failed for string coordinates; pipelines centre on their first point.
//   • Eight unlabeled eye buttons for layers covered the map on small screens — layers now sit in
//     one labelled menu (with counts); the old per-layer buttons stay available via
//     `inlineLayerButtons`.
//   FEATURES
//   • Zoom in / out, compass (north up), keyboard shortcuts (F focus, A AI view, R reset,
//     + / − zoom) that ignore typing in inputs, `isFocusMode` state, layer counts.
// --- END UPDATE ---------------------------------------------------------------------------
import React, { useState } from 'react';
import { Box, VStack, IconButton, Tooltip } from '@chakra-ui/react';
import { FaHome, FaCube, FaMap, FaCrosshairs, FaEye, FaEyeSlash, FaExpand, FaBrain } from 'react-icons/fa';
// --- UPDATE
import { useEffect, useCallback } from 'react';
import { Menu, MenuButton, MenuList, MenuItemOption, MenuOptionGroup, Badge, HStack, Text, Portal, useColorModeValue } from '@chakra-ui/react';
import { FaLayerGroup, FaPlus, FaMinus, FaCompass, FaCompress } from 'react-icons/fa';

const LAYER_LABELS = Object.freeze({
  assets: 'Assets', pipelines: 'Pipelines', sensors: 'Sensors', alerts: 'Active alerts', issues: 'Reported issues',
  openWorkOrders: 'Open work orders', predictedFailures: 'Predicted failures', technicianLocations: 'Technician locations',
  terrain: 'Ground elevation (TessaDEM)', // --- UPDATE (twin v8)
});
const toLngLat = (o) => {
  if (!o) return null;
  const lng = Number(o.longitude ?? o.lng), lat = Number(o.latitude ?? o.lat);
  if (Number.isFinite(lng) && Number.isFinite(lat) && !(lng === 0 && lat === 0)) return [lng, lat];
  let p = o.path_coordinates ?? o.sourceData?.path_coordinates;
  if (typeof p === 'string') { try { p = JSON.parse(p); } catch (_) { p = null; } }
  const c = Array.isArray(p) && p.length ? p[Math.floor(p.length / 2)] : null;
  if (!c) return null;
  const a = Array.isArray(c) ? [Number(c[0]), Number(c[1])] : [Number(c.lng ?? c.longitude), Number(c.lat ?? c.latitude)];
  return Number.isFinite(a[0]) && Number.isFinite(a[1]) ? a : null;
};
const isTyping = (e) => { const t = e.target; return t && (t.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(t.tagName)); };
// --- END UPDATE

const DigitalTwinControls = ({
  map,
  initialViewState,
  selectedObject,
  layerVisibility,
  onLayerToggle,
  toggleFocusMode,
  isAiMode,          // --- NEW PROP ---
  toggleAiMode,      // --- NEW PROP ---
  // --- UPDATE
  onResetView = null,
  isFocusMode = false,
  layerCounts = {},
  inlineLayerButtons = false,
  enableShortcuts = true,
  // --- END UPDATE
}) => {
  const [is3D, setIs3D] = useState(true);
  // --- UPDATE: theme + live pitch sync (hooks before any early return)
  const menuBg = useColorModeValue('white', 'gray.800');
  const [isFullscreen, setIsFullscreen] = useState(false);
  useEffect(() => {
    if (!map || typeof map.on !== 'function') return undefined;
    const sync = () => { try { setIs3D((map.getPitch?.() ?? 0) > 5); } catch (_) { /* map removed */ } };
    sync();
    map.on('pitchend', sync);
    return () => { try { map.off('pitchend', sync); } catch (_) { /* map removed */ } };
  }, [map]);
  useEffect(() => {
    const onFs = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFs);
    return () => document.removeEventListener('fullscreenchange', onFs);
  }, []);
  const focus = useCallback(() => {
    if (typeof toggleFocusMode === 'function') { toggleFocusMode(); return; }
    try {
      if (document.fullscreenElement) document.exitFullscreen?.();
      else (map?.getContainer?.()?.parentElement || document.documentElement).requestFullscreen?.();
    } catch (_) { /* full screen not allowed */ }
  }, [toggleFocusMode, map]);
  const zoomBy = useCallback((dz) => { try { map?.easeTo({ zoom: (map.getZoom?.() ?? 15) + dz, duration: 300 }); } catch (_) { /* noop */ } }, [map]);
  const northUp = useCallback(() => { try { map?.easeTo({ bearing: 0, duration: 600 }); } catch (_) { /* noop */ } }, [map]);
  // --- END UPDATE

  // --- UPDATE: handlers as callbacks so the shortcut effect can use them
  const handleResetView = useCallback(() => {
    if (typeof onResetView === 'function') { onResetView(); }
    else map?.flyTo({ ...initialViewState, duration: 2000 });
    if (!is3D) setIs3D(true);
  }, [onResetView, map, initialViewState, is3D]);

  const handleToggle3D = useCallback(() => {
    map?.flyTo({
      pitch: is3D ? 0 : (initialViewState?.pitch ?? 60),
      bearing: is3D ? 0 : (initialViewState?.bearing ?? 0),
      duration: 1500,
    });
    setIs3D(!is3D);
  }, [map, is3D, initialViewState]);

  const target = toLngLat(selectedObject);
  const handleCenterOnAsset = useCallback(() => {
    if (target) {
      map?.flyTo({
        center: target,
        zoom: 17,
        pitch: is3D ? (initialViewState?.pitch ?? 60) : 0,
        bearing: is3D ? (initialViewState?.bearing ?? 0) : 0,
        duration: 1500,
      });
    }
  }, [map, target, is3D, initialViewState]);

  useEffect(() => {
    if (!enableShortcuts || !map) return undefined;
    const onKey = (e) => {
      if (isTyping(e) || e.ctrlKey || e.metaKey || e.altKey) return;
      const k = e.key.toLowerCase();
      if (k === 'f') { e.preventDefault(); focus(); }
      else if (k === 'a' && toggleAiMode) { e.preventDefault(); toggleAiMode(); }
      else if (k === 'r') { e.preventDefault(); handleResetView(); }
      else if (k === '+' || k === '=') zoomBy(1);
      else if (k === '-') zoomBy(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [enableShortcuts, map, focus, toggleAiMode, handleResetView, zoomBy]);
  // --- END UPDATE

  if (!map) return null;

  // --- UPDATE: layer menu values
  const visibleKeys = Object.entries(layerVisibility || {}).filter(([, v]) => v).map(([k]) => k);
  const setVisibleKeys = (keys) => {
    const next = new Set(Array.isArray(keys) ? keys : [keys]);
    Object.keys(layerVisibility || {}).forEach((k) => { const want = next.has(k); if (!!layerVisibility[k] !== want) onLayerToggle?.(k, want); });
  };
  // --- END UPDATE

  return (
    <Box position="absolute" bottom={{ base: '140px', md: '20px' }} left="20px" zIndex={10}>
      <VStack spacing={2}>
        {/* Reset View */}
        <Tooltip label="Reset View (R)" placement="right">
          <IconButton
            aria-label="Reset View"
            icon={<FaHome />}
            onClick={handleResetView}
            size="lg"
            colorScheme="blue"
            boxShadow="lg"
          />
        </Tooltip>

        {/* Toggle 3D/2D */}
        <Tooltip label={is3D ? 'Switch to 2D View' : 'Switch to 3D View'} placement="right">
          <IconButton
            aria-label="Toggle 3D/2D View"
            icon={is3D ? <FaMap /> : <FaCube />}
            onClick={handleToggle3D}
            size="lg"
            colorScheme="blue"
            boxShadow="lg"
          />
        </Tooltip>

        {/* Center on Selected Asset */}
        <Tooltip label="Center on Selected Asset" placement="right">
          <IconButton
            aria-label="Center on Asset"
            icon={<FaCrosshairs />}
            onClick={handleCenterOnAsset}
            size="lg"
            colorScheme="blue"
            boxShadow="lg"
            isDisabled={!selectedObject || !target}
          />
        </Tooltip>

        {/* --- UPDATE: zoom + compass */}
        <HStack spacing={1}>
          <Tooltip label="Zoom in (+)" placement="top"><IconButton aria-label="Zoom in" icon={<FaPlus />} size="sm" colorScheme="blue" variant="solid" boxShadow="md" onClick={() => zoomBy(1)} /></Tooltip>
          <Tooltip label="Zoom out (−)" placement="top"><IconButton aria-label="Zoom out" icon={<FaMinus />} size="sm" colorScheme="blue" variant="solid" boxShadow="md" onClick={() => zoomBy(-1)} /></Tooltip>
        </HStack>
        <Tooltip label="North up" placement="right"><IconButton aria-label="North up" icon={<FaCompass />} size="sm" colorScheme="blue" variant="outline" bg={menuBg} boxShadow="md" onClick={northUp} /></Tooltip>
        {/* --- END UPDATE */}

        {/* --- NEW: AI Predictive View Toggle --- */}
        <Tooltip label={isAiMode ? "Disable AI Predictive View (A)" : "Enable AI Predictive View (A)"} placement="right">
          <IconButton
            aria-label="Toggle AI View"
            aria-pressed={!!isAiMode} // --- UPDATE
            icon={<FaBrain />}
            onClick={toggleAiMode}
            size="lg"
            colorScheme={isAiMode ? "purple" : "gray"}
            boxShadow="lg"
            borderWidth={isAiMode ? "2px" : "0"}
            borderColor="purple.300"
          />
        </Tooltip>

        {/* --- UPDATE: one labelled layer menu */}
        {layerVisibility && !inlineLayerButtons && (
          <Menu closeOnSelect={false} placement="right-end">
            <Tooltip label="Map layers" placement="right">
              <MenuButton as={IconButton} aria-label="Map layers" icon={<FaLayerGroup />} size="lg" colorScheme="green" boxShadow="lg" />
            </Tooltip>
            <Portal>
              <MenuList bg={menuBg} minW="240px" zIndex={1500}>
                <MenuOptionGroup type="checkbox" title={`Layers (${visibleKeys.length}/${Object.keys(layerVisibility).length})`} value={visibleKeys} onChange={setVisibleKeys}>
                  {Object.keys(layerVisibility).map((k) => (
                    <MenuItemOption key={k} value={k} fontSize="sm">
                      <HStack justify="space-between" w="100%"><Text>{LAYER_LABELS[k] || k}</Text>{layerCounts?.[k] !== undefined && <Badge borderRadius="full">{layerCounts[k]}</Badge>}</HStack>
                    </MenuItemOption>
                  ))}
                </MenuOptionGroup>
              </MenuList>
            </Portal>
          </Menu>
        )}
        {/* --- END UPDATE */}

        {/* Layer Visibility Toggles */}
        {layerVisibility && inlineLayerButtons && /* --- UPDATE: opt-in */
          Object.entries(layerVisibility).map(([layer, visible]) => (
            <Tooltip key={layer} label={`Toggle ${layer}`} placement="right">
              <IconButton
                aria-label={`Toggle ${layer}`}
                icon={visible ? <FaEye /> : <FaEyeSlash />}
                onClick={() => onLayerToggle(layer, !visible)} // --- UPDATE: explicit value
                size="md"
                colorScheme={visible ? 'green' : 'gray'}
                boxShadow="md"
              />
            </Tooltip>
          ))}

        {/* Focus Mode */}
        <Tooltip label={isFocusMode || isFullscreen ? 'Exit Focus Mode (F / Esc)' : 'Toggle Focus Mode (F)'} placement="right">
          <IconButton
            aria-label="Focus Mode"
            aria-pressed={!!(isFocusMode || isFullscreen)} // --- UPDATE
            icon={isFocusMode || isFullscreen ? <FaCompress /> : <FaExpand />}
            onClick={focus} // --- UPDATE: full-screen fallback
            size="lg"
            colorScheme="purple"
            boxShadow="lg"
          />
        </Tooltip>
      </VStack>
    </Box>
  );
};

export default DigitalTwinControls;
